import { useState, useEffect } from "react";
import { 
  FaDownload, 
  FaSearch, 
  FaEye, 
  FaSpinner, 
  FaSync, 
  FaEnvelope, 
  FaPhone, 
  FaUsers, 
  FaBoxOpen,
  FaTrash,
  FaTimes,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";
import { useAuth } from './AuthContext';
import "../DataPage.css";

function DataPage({ addToast }) {
  const { user, canDelete, canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState("popInquiry");
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [counts, setCounts] = useState({
    popInquiry: 0,
    inquiry: 0,
    contactForm: 0,
    bulkInquiry: 0
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Different API endpoints for different inquiry types
  const endpoints = {
    popInquiry: 'https://airexheater.in/api/popInquiry',
    inquiry: 'https://airexheater.in/api/inquiry',
    contactForm: 'https://airexheater.in/api/contact',
    bulkInquiry: 'https://airexheater.in/api/bulk'
  };

  // Reset to first page when tab changes or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // Fetch data when tab changes
  useEffect(() => {
    fetchInquiries();
    fetchAllCounts();
  }, [activeTab]);

  const fetchAllCounts = async () => {
    for (const [key, endpoint] of Object.entries(endpoints)) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const count = Array.isArray(data) ? data.length : (data.data?.length || 0);
          setCounts(prev => ({ ...prev, [key]: count }));
        }
      } catch (err) {
        console.error(`Error fetching ${key} count:`, err);
      }
    }
  };

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = endpoints[activeTab];
      console.log(`Fetching from: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`${activeTab} data:`, data);
      
      let inquiriesData = [];
      if (Array.isArray(data)) {
        inquiriesData = data;
      } else if (data.data && Array.isArray(data.data)) {
        inquiriesData = data.data;
      } else if (data.inquiries && Array.isArray(data.inquiries)) {
        inquiriesData = data.inquiries;
      } else if (typeof data === 'object') {
        inquiriesData = [data];
      }
      
      setInquiries(inquiriesData);
      addToast(`Successfully loaded ${inquiriesData.length} ${activeTab} records`, 'success', 3000);
      
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
      addToast(`Failed to load ${activeTab} data: ${err.message}`, 'error', 4000);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete inquiry function
  const handleDelete = async (id) => {
    if (!canDelete()) {
      addToast('Access Denied! Only Super Admin can delete inquiries.', 'error', 3000);
      setDeleteConfirm(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = `${endpoints[activeTab]}/${id}`;
      console.log(`Deleting from: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      // Remove from local state
      setInquiries(inquiries.filter(inquiry => inquiry._id !== id));
      addToast(`Successfully deleted ${activeTab} record`, 'success', 3000);
      
      // Refresh counts
      fetchAllCounts();
      
      // Adjust current page if needed
      const newTotalItems = inquiries.length - 1;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
      
    } catch (err) {
      console.error("Delete error:", err);
      addToast(`Failed to delete: ${err.message}`, 'error', 4000);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const tabs = [
    { id: "popInquiry", label: "POP Inquiry", icon: <FaBoxOpen />, color: "#3498db" },
    { id: "inquiry", label: "General Inquiry", icon: <FaEnvelope />, color: "#27ae60" },
    { id: "contactForm", label: "Contact Form", icon: <FaPhone />, color: "#e67e22" },
    { id: "bulkInquiry", label: "Bulk Inquiry", icon: <FaUsers />, color: "#9b59b6" }
  ];

  // Filter inquiries based on search
  const filteredInquiries = inquiries.filter(inquiry => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    if (activeTab === "popInquiry") {
      const fullName = `${inquiry.firstName || ''} ${inquiry.lastName || ''}`.toLowerCase();
      return fullName.includes(searchLower) ||
             (inquiry.userMail || '').toLowerCase().includes(searchLower) ||
             (inquiry.userPhone || '').toString().includes(searchTerm) ||
             (inquiry.userSubject || '').toLowerCase().includes(searchLower);
    } 
    else if (activeTab === "inquiry") {
      return (inquiry.userName || '').toLowerCase().includes(searchLower) ||
             (inquiry.userEmail || '').toLowerCase().includes(searchLower) ||
             (inquiry.userPhone || '').toString().includes(searchTerm) ||
             (inquiry.company || '').toLowerCase().includes(searchLower) ||
             (inquiry.trade || '').toLowerCase().includes(searchLower);
    }
    else if (activeTab === "contactForm") {
      return (inquiry.name || '').toLowerCase().includes(searchLower) ||
             (inquiry.email || '').toLowerCase().includes(searchLower) ||
             (inquiry.phone || '').toString().includes(searchTerm) ||
             (inquiry.company || '').toLowerCase().includes(searchLower);
    }
    else if (activeTab === "bulkInquiry") {
      return (inquiry.fullName || '').toLowerCase().includes(searchLower) ||
             (inquiry.email || '').toLowerCase().includes(searchLower) ||
             (inquiry.phoneNumber || '').toString().includes(searchTerm) ||
             (inquiry.company || '').toLowerCase().includes(searchLower);
    }
    return true;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredInquiries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Generate page numbers
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  // Export to CSV
  const handleExportCSV = () => {
    let headers = [];
    let csvData = [];
    
    if (activeTab === "popInquiry") {
      headers = ["ID", "Full Name", "Email", "Phone", "Subject", "Message", "Date"];
      csvData = filteredInquiries.map(inquiry => [
        inquiry._id,
        `${inquiry.firstName || ''} ${inquiry.lastName || ''}`.trim(),
        inquiry.userMail || '',
        inquiry.userPhone || '',
        inquiry.userSubject || '',
        `"${(inquiry.message || '').replace(/"/g, '""')}"`,
        new Date(inquiry.createdAt).toLocaleString()
      ]);
    } 
    else if (activeTab === "inquiry") {
      headers = ["ID", "Name", "Email", "Phone", "Company", "Trade", "Country", "State", "Message", "Date"];
      csvData = filteredInquiries.map(inquiry => [
        inquiry._id,
        inquiry.userName || '',
        inquiry.userEmail || '',
        inquiry.userPhone || '',
        inquiry.company || '',
        inquiry.trade || '',
        inquiry.country || '',
        inquiry.state || '',
        `"${(inquiry.message || '').replace(/"/g, '""')}"`,
        new Date(inquiry.createdAt).toLocaleString()
      ]);
    }
    else if (activeTab === "contactForm") {
      headers = ["ID", "Name", "Email", "Phone", "Company", "Trade", "Country", "Message", "Date"];
      csvData = filteredInquiries.map(inquiry => [
        inquiry._id,
        inquiry.name || '',
        inquiry.email || '',
        inquiry.phone || '',
        inquiry.company || '',
        inquiry.trade || '',
        inquiry.country || '',
        `"${(inquiry.message || '').replace(/"/g, '""')}"`,
        new Date(inquiry.createdAt).toLocaleString()
      ]);
    }
    else if (activeTab === "bulkInquiry") {
      headers = ["ID", "Full Name", "Email", "Phone Number", "Company", "Message", "File Upload", "Date"];
      csvData = filteredInquiries.map(inquiry => [
        inquiry._id,
        inquiry.fullName || '',
        inquiry.email || '',
        inquiry.phoneNumber || '',
        inquiry.company || '',
        `"${(inquiry.message || '').replace(/"/g, '""')}"`,
        inquiry.fileUpload || '',
        new Date(inquiry.createdAt).toLocaleString()
      ]);
    }
    
    const csvContent = [headers, ...csvData].map(row => row.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addToast(`Successfully exported ${filteredInquiries.length} records to CSV`, 'success', 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getFullName = (inquiry) => {
    if (activeTab === "popInquiry") {
      return `${inquiry.firstName || ''} ${inquiry.lastName || ''}`.trim() || 'N/A';
    } else if (activeTab === "inquiry") {
      return inquiry.userName || 'N/A';
    } else if (activeTab === "contactForm") {
      return inquiry.name || 'N/A';
    } else if (activeTab === "bulkInquiry") {
      return inquiry.fullName || 'N/A';
    }
    return 'N/A';
  };

  const renderTableHeaders = () => {
    if (activeTab === "popInquiry") {
      return (
        <tr>
          <th>S.No</th>
          <th>Full Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Subject</th>
          <th>Message</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      );
    } 
    else if (activeTab === "inquiry") {
      return (
        <tr>
          <th>S.No</th>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Company</th>
          <th>Trade</th>
          <th>Country</th>
          <th>State</th>
          <th>Message</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      );
    }
    else if (activeTab === "contactForm") {
      return (
        <tr>
          <th>S.No</th>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Company</th>
          <th>Trade</th>
          <th>Country</th>
          <th>Message</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      );
    }
    else if (activeTab === "bulkInquiry") {
      return (
        <tr>
          <th>S.No</th>
          <th>Full Name</th>
          <th>Email</th>
          <th>Phone Number</th>
          <th>Company</th>
          <th>Message</th>
          <th>File</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      );
    }
  };

  const renderTableRow = (inquiry, index) => {
    const fullName = getFullName(inquiry);
    const serialNumber = indexOfFirstItem + index + 1;
    const hasDeletePermission = canDelete();
    
    if (activeTab === "popInquiry") {
      return (
        <tr key={inquiry._id || index}>
          <td>{serialNumber}</td>
          <td><strong>{fullName}</strong></td>
          <td>{inquiry.userMail || 'N/A'}</td>
          <td>{inquiry.userPhone || 'N/A'}</td>
          <td><span className="subject-badge">{inquiry.userSubject || 'N/A'}</span></td>
          <td className="message-cell">{inquiry.message ? (inquiry.message.length > 50 ? `${inquiry.message.substring(0, 50)}...` : inquiry.message) : 'No message'}</td>
          <td>{formatDate(inquiry.createdAt)}</td>
          <td>
            <button className="action-btn view" onClick={() => setSelectedInquiry(inquiry)} title="View Details">
              <FaEye />
            </button>
            <button 
              className={`action-btn delete ${!hasDeletePermission ? 'disabled' : ''}`} 
              onClick={() => hasDeletePermission ? setDeleteConfirm(inquiry) : addToast('Access Denied! Only Super Admin can delete.', 'error', 3000)}
              title={hasDeletePermission ? "Delete" : "Only Super Admin can delete"}
            >
              <FaTrash />
            </button>
          </td>
        </tr>
      );
    }
    else if (activeTab === "inquiry") {
      return (
        <tr key={inquiry._id || index}>
          <td>{serialNumber}</td>
          <td><strong>{fullName}</strong></td>
          <td>{inquiry.userEmail || 'N/A'}</td>
          <td>{inquiry.userPhone || 'N/A'}</td>
          <td>{inquiry.company || 'N/A'}</td>
          <td>{inquiry.trade || 'N/A'}</td>
          <td>{inquiry.country || 'N/A'}</td>
          <td>{inquiry.state || 'N/A'}</td>
          <td className="message-cell">{inquiry.message ? (inquiry.message.length > 50 ? `${inquiry.message.substring(0, 50)}...` : inquiry.message) : 'No message'}</td>
          <td>{formatDate(inquiry.createdAt)}</td>
          <td>
            <button className="action-btn view" onClick={() => setSelectedInquiry(inquiry)} title="View Details">
              <FaEye />
            </button>
            <button 
              className={`action-btn delete ${!hasDeletePermission ? 'disabled' : ''}`} 
              onClick={() => hasDeletePermission ? setDeleteConfirm(inquiry) : addToast('Access Denied! Only Super Admin can delete.', 'error', 3000)}
              title={hasDeletePermission ? "Delete" : "Only Super Admin can delete"}
            >
              <FaTrash />
            </button>
          </td>
        </tr>
      );
    }
    else if (activeTab === "contactForm") {
      return (
        <tr key={inquiry._id || index}>
          <td>{serialNumber}</td>
          <td><strong>{fullName}</strong></td>
          <td>{inquiry.email || 'N/A'}</td>
          <td>{inquiry.phone || 'N/A'}</td>
          <td>{inquiry.company || 'N/A'}</td>
          <td>{inquiry.trade || 'N/A'}</td>
          <td>{inquiry.country || 'N/A'}</td>
          <td className="message-cell">{inquiry.message ? (inquiry.message.length > 50 ? `${inquiry.message.substring(0, 50)}...` : inquiry.message) : 'No message'}</td>
          <td>{formatDate(inquiry.createdAt)}</td>
          <td>
            <button className="action-btn view" onClick={() => setSelectedInquiry(inquiry)} title="View Details">
              <FaEye />
            </button>
            <button 
              className={`action-btn delete ${!hasDeletePermission ? 'disabled' : ''}`} 
              onClick={() => hasDeletePermission ? setDeleteConfirm(inquiry) : addToast('Access Denied! Only Super Admin can delete.', 'error', 3000)}
              title={hasDeletePermission ? "Delete" : "Only Super Admin can delete"}
            >
              <FaTrash />
            </button>
          </td>
        </tr>
      );
    }
    else if (activeTab === "bulkInquiry") {
      return (
        <tr key={inquiry._id || index}>
          <td>{serialNumber}</td>
          <td><strong>{fullName}</strong></td>
          <td>{inquiry.email || 'N/A'}</td>
          <td>{inquiry.phoneNumber || 'N/A'}</td>
          <td>{inquiry.company || 'N/A'}</td>
          <td className="message-cell">{inquiry.message ? (inquiry.message.length > 50 ? `${inquiry.message.substring(0, 50)}...` : inquiry.message) : 'No message'}</td>
          <td>
            {inquiry.fileUpload ? (
              <a href={inquiry.fileUpload} target="_blank" rel="noopener noreferrer" className="file-link">
                View File
              </a>
            ) : 'No file'}
          </td>
          <td>{formatDate(inquiry.createdAt)}</td>
          <td>
            <button className="action-btn view" onClick={() => setSelectedInquiry(inquiry)} title="View Details">
              <FaEye />
            </button>
            <button 
              className={`action-btn delete ${!hasDeletePermission ? 'disabled' : ''}`} 
              onClick={() => hasDeletePermission ? setDeleteConfirm(inquiry) : addToast('Access Denied! Only Super Admin can delete.', 'error', 3000)}
              title={hasDeletePermission ? "Delete" : "Only Super Admin can delete"}
            >
              <FaTrash />
            </button>
          </td>
        </tr>
      );
    }
  };

  return (
    <div className="data-page">
      {/* Stats Cards */}
      <div className="stats-container">
        {tabs.map(tab => (
          <div 
            key={tab.id}
            className={`stat-card ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{ borderTopColor: tab.color }}
          >
            <div className="stat-icon" style={{ color: tab.color }}>
              {tab.icon}
            </div>
            <div className="stat-info">
              <h3>{tab.label}</h3>
              <p className="stat-count">{counts[tab.id] || 0} Entries</p>
            </div>
            <div className="stat-arrow">→</div>
          </div>
        ))}
      </div>

      {/* Data Table Section */}
      <div className="data-content">
        <div className="data-header">
          <div>
            <h2>{tabs.find(t => t.id === activeTab)?.label} Details</h2>
            <p className="total-count">Total: {filteredInquiries.length} entries</p>
          </div>
          <div className="header-buttons">
            <button onClick={fetchInquiries} className="refresh-btn" title="Refresh data">
              <FaSync /> Refresh
            </button>
            <button onClick={handleExportCSV} className="export-btn" title="Export to CSV">
              <FaDownload /> Export CSV
            </button>
          </div>
        </div>

        <div className="filters-section">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search by name, email, phone, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm("")}>×</button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <div><strong>⚠️ Error:</strong> {error}</div>
            <button onClick={fetchInquiries}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading {tabs.find(t => t.id === activeTab)?.label} data...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  {renderTableHeaders()}
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === "inquiry" ? 11 : (activeTab === "contactForm" ? 10 : (activeTab === "bulkInquiry" ? 9 : 8))} className="no-data">
                        {searchTerm ? 'No matching entries found' : `No ${tabs.find(t => t.id === activeTab)?.label} data available`}
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((inquiry, index) => renderTableRow(inquiry, index))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredInquiries.length > itemsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredInquiries.length)} of {filteredInquiries.length} entries
                </div>
                <div className="pagination-controls">
                  <button 
                    onClick={prevPage} 
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  
                  <div className="page-numbers">
                    {getPageNumbers().map((number, index) => (
                      number === '...' ? (
                        <span key={index} className="page-dots">...</span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => paginate(number)}
                          className={`page-number ${currentPage === number ? 'active' : ''}`}
                        >
                          {number}
                        </button>
                      )
                    ))}
                  </div>
                  
                  <button 
                    onClick={nextPage} 
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this inquiry?</p>
              <div className="delete-preview">
                <strong>Name:</strong> {getFullName(deleteConfirm)}
                <br />
                <strong>Email:</strong> {deleteConfirm.userMail || deleteConfirm.userEmail || deleteConfirm.email}
                <br />
                <strong>Type:</strong> {tabs.find(t => t.id === activeTab)?.label}
              </div>
              <div className="delete-actions">
                <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={() => handleDelete(deleteConfirm._id)}>
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {selectedInquiry && (
        <div className="modal-overlay" onClick={() => setSelectedInquiry(null)}>
          <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Inquiry Details</h2>
              <button className="close-btn" onClick={() => setSelectedInquiry(null)}>×</button>
            </div>
            <div className="modal-body">
              {activeTab === "popInquiry" && (
                <>
                  <div className="detail-row"><strong>Full Name:</strong> <span>{`${selectedInquiry.firstName || ''} ${selectedInquiry.lastName || ''}`.trim()}</span></div>
                  <div className="detail-row"><strong>Email:</strong> <span>{selectedInquiry.userMail}</span></div>
                  <div className="detail-row"><strong>Phone:</strong> <span>{selectedInquiry.userPhone}</span></div>
                  <div className="detail-row"><strong>Subject:</strong> <span>{selectedInquiry.userSubject}</span></div>
                  <div className="detail-row"><strong>Message:</strong> <p className="message-text">{selectedInquiry.message || 'No message'}</p></div>
                  <div className="detail-row"><strong>Date:</strong> <span>{formatDate(selectedInquiry.createdAt)}</span></div>
                  <div className="detail-row"><strong>ID:</strong> <code>{selectedInquiry._id}</code></div>
                </>
              )}
              {activeTab === "inquiry" && (
                <>
                  <div className="detail-row"><strong>Name:</strong> <span>{selectedInquiry.userName}</span></div>
                  <div className="detail-row"><strong>Email:</strong> <span>{selectedInquiry.userEmail}</span></div>
                  <div className="detail-row"><strong>Phone:</strong> <span>{selectedInquiry.userPhone}</span></div>
                  <div className="detail-row"><strong>Company:</strong> <span>{selectedInquiry.company || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Trade:</strong> <span>{selectedInquiry.trade || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Country:</strong> <span>{selectedInquiry.country || 'N/A'}</span></div>
                  <div className="detail-row"><strong>State:</strong> <span>{selectedInquiry.state || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Message:</strong> <p className="message-text">{selectedInquiry.message || 'No message'}</p></div>
                  <div className="detail-row"><strong>Date:</strong> <span>{formatDate(selectedInquiry.createdAt)}</span></div>
                </>
              )}
              {activeTab === "contactForm" && (
                <>
                  <div className="detail-row"><strong>Name:</strong> <span>{selectedInquiry.name}</span></div>
                  <div className="detail-row"><strong>Email:</strong> <span>{selectedInquiry.email}</span></div>
                  <div className="detail-row"><strong>Phone:</strong> <span>{selectedInquiry.phone}</span></div>
                  <div className="detail-row"><strong>Company:</strong> <span>{selectedInquiry.company || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Trade:</strong> <span>{selectedInquiry.trade || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Country:</strong> <span>{selectedInquiry.country || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Message:</strong> <p className="message-text">{selectedInquiry.message || 'No message'}</p></div>
                  <div className="detail-row"><strong>Date:</strong> <span>{formatDate(selectedInquiry.createdAt)}</span></div>
                </>
              )}
              {activeTab === "bulkInquiry" && (
                <>
                  <div className="detail-row"><strong>Full Name:</strong> <span>{selectedInquiry.fullName}</span></div>
                  <div className="detail-row"><strong>Email:</strong> <span>{selectedInquiry.email}</span></div>
                  <div className="detail-row"><strong>Phone Number:</strong> <span>{selectedInquiry.phoneNumber}</span></div>
                  <div className="detail-row"><strong>Company:</strong> <span>{selectedInquiry.company || 'N/A'}</span></div>
                  <div className="detail-row"><strong>Message:</strong> <p className="message-text">{selectedInquiry.message || 'No message'}</p></div>
                  <div className="detail-row"><strong>File Upload:</strong> {selectedInquiry.fileUpload ? <a href={selectedInquiry.fileUpload} target="_blank" rel="noopener noreferrer" className="file-download-link">Download File</a> : 'No file uploaded'}</div>
                  <div className="detail-row"><strong>Date:</strong> <span>{formatDate(selectedInquiry.createdAt)}</span></div>
                  <div className="detail-row"><strong>ID:</strong> <code>{selectedInquiry._id}</code></div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataPage;